import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import './AddCustomColumnDialog.css';

const AddCustomColumnDialog = ({ isModalOpen, onClose, onAddColumn }) => {
    const defaultColumnConfig = {
        caption: '',
        type: 'number',
        sourceField: 'totalDigitalCredits',
        coefficient: -1,
        uniformValues: true,
        initValue: ''
    };

    let formikInstance;

    const validationSchema = Yup.object({
        caption: Yup.string()
            .required('Caption is required.')
            .min(1, 'Caption should be at least 1 character.')
            .max(20, 'Caption should be max 20 characters.'),
        initValue: Yup.number('Initial value should be a positive number.')
            .required('Initial value is required.')
            .min(0, 'Initial value should be zero or a positive number.')
            .test(
                'is-percentage-valid',
                'Percentage should be a number between 0 and 100',
                function (value) {
                    const { type } = this.parent;
                    return type !== 'percentage' || (value >= 0 && value <= 100);
                }
            )
    });
    
    useEffect(() => {
        const handleKeydown = (event) => {
            switch (event.keyCode) {
                case 27: // ESC key
                    onClose();
                    break;
                case 13: // Enter key
                    if (formikInstance) {
                        formikInstance.submitForm();
                    }
                    break;
                default:
                    // Do nothing for other keys
                    break;
            }
        };

        if (isModalOpen) {
            // Attach the event listener when the modal is open
            document.addEventListener('keydown', handleKeydown);
        } else {
            // Remove the event listener when the modal is closed
            document.removeEventListener('keydown', handleKeydown);
        }

        return () => {
            // Clean up the event listener on component unmount
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [isModalOpen, onClose, formikInstance]);

    return (
        isModalOpen && (
            <div className="modal">
                <div className="modal-content">
                    <span className="close" onClick={onClose}>&times;</span>
                    <div>
                        <h3 className="dialogText">Add Custom Column</h3>
                    </div>
                    <Formik
                        innerRef={(instance) => { formikInstance = instance; }}
                        initialValues={defaultColumnConfig}
                        validationSchema={validationSchema}
                        onSubmit={values => onAddColumn(values)}
                    >
                        {formik => {
                            // Define the function here so it has access to the formik object.
                            function getClassNameForParentOfField(fieldName, defaultClass) {
                                if (formik.touched[fieldName] && formik.errors[fieldName]) {
                                    return `${defaultClass} input-error-parent`;
                                }
                                return defaultClass;
                            }

                            return (
                                <Form id="addCustomColumnDialogue">
                                    <div className="modal-row">
                                        <label htmlFor="fieldCaptionInput" className="modal-label">Caption:</label>
                                        <div className={getClassNameForParentOfField('caption', 'modal-input')}>
                                            <TextBoxComponent
                                                id="fieldCaptionInput"
                                                className="modal-input-real"
                                                placeholder="Field Caption"
                                                value={formik.values.caption}
                                                change={e => formik.setFieldValue('caption', e.value)}
                                            />
                                        </div>
                                    </div>
                                    {formik.touched.caption && formik.errors.caption ? (
                                        <div className="error-message">{formik.errors.caption}</div>
                                    ) : null}

                                    <div className="modal-row">
                                        <label htmlFor="dataTypeDropdown" className="modal-label">Data Type:</label>
                                        <DropDownListComponent
                                            id="dataTypeDropdown"
                                            className="modal-input"
                                            dataSource={['number', 'percentage']}
                                            value={formik.values.type}
                                            change={e => formik.setFieldValue('type', e.value)}
                                        />
                                    </div>

                                    {formik.values.type === 'percentage' && (
                                        <div className="modal-row">
                                            <label htmlFor="sourceFieldDropdown" className="modal-label">Source Field:</label>
                                            <DropDownListComponent
                                                id="sourceFieldDropdown"
                                                className="modal-input"
                                                dataSource={[
                                                    { text: 'Total Digital Fare', value: 'totalDigitalCredits' },
                                                    { text: 'Total Collected Fare', value: 'totalCollectedFare' },
                                                    { text: 'Total Cash Payment', value: 'totalCashPayment' }
                                                ]}
                                                fields={{ text: 'text', value: 'value' }}
                                                value={formik.values.sourceField}
                                                change={e => formik.setFieldValue('sourceField', e.value)}
                                            />
                                        </div>
                                    )}

                                    <div className="modal-row">
                                        <label htmlFor="coefficientDropdown" className="modal-label">Operand:</label>
                                        <DropDownListComponent
                                            id="coefficientDropdown"
                                            className="modal-input"
                                            dataSource={[
                                                { text: 'subtract', value: -1 },
                                                { text: 'add', value: 1 }
                                            ]}
                                            fields={{ text: 'text', value: 'value' }}
                                            value={formik.values.coefficient}
                                            change={(e) => formik.setFieldValue('coefficient', Number(e.value))}
                                        />
                                    </div>

                                    <div className="modal-row">
                                        <label htmlFor="initialValueInput" className="modal-label">Initial Value:</label>
                                        <div className={getClassNameForParentOfField('initValue', 'modal-input')}>
                                        <TextBoxComponent
                                            id="initialValueInput"
                                            className="modal-input-real"
                                            placeholder={`Enter initial ${formik.values.type === 'percentage' ? 'percentage' : 'number'}`}
                                            value={formik.values.initValue}
                                            change={e => formik.setFieldValue('initValue', e.value)}
                                                hint={formik.values.type === 'percentage' ? 'Percentage for initial column value' : 'Number for initial column value'}
                                        />
                                        </div>
                                    </div>
                                    {formik.touched.initValue && formik.errors.initValue ? (
                                        <div className="error-message">{formik.errors.initValue}</div>
                                    ) : null}

                                    <div className="modal-row">
                                        <label className="modal-label">Bind Rows:</label>
                                        <CheckBoxComponent
                                            id="uniformValuesCheckbox"
                                            className="modal-input"
                                            label='Uniform Column Values'
                                            checked={formik.values.uniformValues}
                                            change={e => formik.setFieldValue('uniformValues', e.checked)}
                                        />
                                    </div>

                                    <div className="modal-row">
                                        <label className="modal-label"></label>
                                        <div className="button-group">
                                            <ButtonComponent type="button" id="cancelButton" onClick={onClose}>Cancel</ButtonComponent>
                                            <ButtonComponent type="submit" id="insertButton">Insert</ButtonComponent>
                                        </div>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>
                </div>
            </div>
        )
    );
};

export default AddCustomColumnDialog;